import { ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, HostListener, inject, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import {
  MatDialog,
  MatDialogRef
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BusinessObjectType } from '@core/enums/business-object-type.enum';
import { DictionaryKeyEnum } from '@core/enums/dictionary-key.enum';
import { DictionaryModel, DictionarySet } from '@core/models/dictionary.model';
import { Panel } from '@core/models/panel';
import { EventDto } from '@core/models/planner-models/event.dto';
import { PlannerRouteDto } from '@core/models/planner-models/planner.model';
import { RegionModel } from '@core/models/region.model';
import { ScheduleAbsenceDto } from '@core/models/scheduling/schedule-absence.dto';
import { ObjectIDModel } from '@core/models/scheduling/scheduling.request';
import { CalendarNativeService } from '@core/services/data-services/calendar.native.service';
import { DeliveryServiceNativeService } from '@core/services/data-services/delivery-service.native.service';
import { DictionaryNativeService } from '@core/services/data-services/dictionary.native.service';
import { PlannerNativeService } from '@core/services/data-services/planner.native.service';
import { RegionsNativeService } from '@core/services/data-services/regions.native.service';
import { SchedulingNativeService } from '@core/services/data-services/scheduling.native.service';
import { DialogsService } from '@core/services/dialogs-service';
import { MessagesService } from '@core/services/messages.service';
import { OrdersService } from '@core/services/orders.service';
import { PanelsService } from '@core/services/panels.service';
import { PermissionsService } from '@core/services/permissions.service';
import { ShipmentsService } from '@core/services/shipments.service';
import { RootScopeService } from '@core/services/upgraded-providers/wrappers/root-scope.service';
import { ROOT_SCOPE_VARIABLES } from '@core/settings/root-scope.settings';
import { addDays, differenceInDays, getStartOfWeek, setToEndOfDay, setToStartOfDay, setToStartOfNextDay } from '@core/utils/date.utils';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, forkJoin, iif, interval, map, mergeMap, Observable, of, startWith, Subject, switchMap, tap, withLatestFrom } from 'rxjs';
import { DaysModeEnum, MiddleDayMap, PeriodsOffsetMap } from '../../../_enums/days-mode.enum';
import { OverlayStatusEnum } from '../../../_enums/overlay-status.enum';
import { AddAbsenceForPlannerComponent } from '../../dialogs/add-absence-for-planner/add-absence-for-planner.component';
import { DragObjectTypeEnum } from '../../enums/drag-object-type.enum';
import { AddAbsenceDialogDataModel } from '../../models/add-absence-dialog-data.model';
import { AddAbsenceDialogResponseModel } from '../../models/add-absence-dialog-response.model';
import { DayModel } from '../../models/day-model';
import { DriverVehiclePairModel, mapDriverResponseToUIResponse } from '../../models/driver-vehicle-pair.model';
import { GridRowModel } from '../../models/grid-row-model';
import { OrderDragModel, OrderDroppedEvent } from '../../models/order-drag-and-drop.model';
import { mapOrderResponseToUIResponse, OrderModel } from '../../models/order.model';
import { PlannerConfiguration } from '../../models/planner-configuration.model';
import { RouteModel } from '../../models/route.model';
import { ShipmentChangeModel } from '../../models/simple-change.model';
import { PlannerDraggingManagerService } from '../../services/planner-draggin-manager.service';
import { PlannerManagerService } from '../../services/planner-manager.service';
import { AddNewAbsenceEvent } from '../empty-day-box/empty-day-box.component';
import { A18SharedDataService } from '@core/services/downgraded-services/ng-shared-data.service';
import { cloneDeep, throttle } from 'lodash';

type CategoryFilterType = { ids: number[], keyNames: string[], title: string, isSelected?: boolean }
const soloAndBusCategoryFilter: CategoryFilterType = { ids: [], keyNames: ['Solówki', 'Busy'], title: 'Solówki i Busy', isSelected: true };
const predefinedCategoryKeyNames = ['Solówki', 'Busy', 'Firanki', 'Hakowce', 'Ruchome podlogi'];

@Component({
  selector: 'planner-view',
  standalone: false,
  templateUrl: './planner-view.component.html',
  styleUrls: ['./planner-view.component.scss'],
  providers: [ShipmentsService, PanelsService, OrdersService]
})
export class PlannerViewComponent implements OnInit, OnDestroy {
  static SharedIdSeq: number = 1;

  @Input() clientId!: number; // dane z bindingu w starym ng 

  @Output() orderPanelsChange = new EventEmitter<Panel>();
  @Output() shipmentPanelsChange = new EventEmitter<Panel>();
  @Output() selectedRouteChanged = new EventEmitter<any>();

  @ViewChild('mainGrid') scrollContainerRef!: ElementRef;

  private scrollInterval: any = null;
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  availableOrders$ = new BehaviorSubject<OrderModel[]>([]);
  availableShipments$ = new BehaviorSubject<OrderModel[]>([]);
  availableOrdersFiltered$!: Observable<OrderModel[]>;
  availableShipmentsFiltered$!: Observable<OrderModel[]>;
  isLoadingShipments = true;
  isLoadingOrders = true;
  taskOrderTypeSet$ = new BehaviorSubject<DictionarySet | null>(null);
  orderStatusSet$ = new BehaviorSubject<DictionarySet | null>(null);
  vehicleCategory$ = new BehaviorSubject<DictionarySet | null>(null);
  vehicleCategoryList$ = new BehaviorSubject<DictionaryModel[] | null>(null);
  vehicleCategoryFilter$ = new BehaviorSubject<CategoryFilterType[] | null>(null);

  inaccessibilityType$ = new BehaviorSubject<DictionaryModel[] | null>(null);
  inaccessibilityStatus$ = new BehaviorSubject<DictionaryModel[] | null>(null);

  regions$ = new BehaviorSubject<RegionModel[] | null>(null);
  gridRows$ = new BehaviorSubject<GridRowModel[]>([]);
  gridRowsFiltered$ = new BehaviorSubject<GridRowModel[]>([]);

  transportAssets$ = new BehaviorSubject<{
    driversIds: number[],
    vehiclesIds: number[],
    trailersIds: number[]
  }>({
    driversIds: [], vehiclesIds: [], trailersIds: []
  });

  displayedDays$ = new BehaviorSubject<Date[]>([]);
  isShipmentDragOverShipments = false;
  isOrderDragOverShipments = false;


  plannerConfiguration$ = new BehaviorSubject<PlannerConfiguration>({
    daysMode: DaysModeEnum.WEEK_WORKING_DAYS,
    firstDay: getStartOfWeek(new Date()),
    lastDay: addDays(getStartOfWeek(new Date()), PeriodsOffsetMap[DaysModeEnum.WEEK_WORKING_DAYS]),
    middleDayOfThePeriod: MiddleDayMap[DaysModeEnum.WEEK_WORKING_DAYS],
    clientId: -1,
  });

  daysModeOptions = [
    { value: DaysModeEnum.WEEK_WORKING_DAYS, displayName: 'Dni robocze' },
    { value: DaysModeEnum.WEEK, displayName: 'Tydzień' },
    { value: DaysModeEnum.DAY, displayName: 'Dzień' },
  ];

  driverSearchControl = new FormControl<string>('');
  ordersSearchControl = new FormControl<string>('');
  mainSearchControl = new FormControl<string>('');

  overlayStatus$ = new BehaviorSubject<OverlayStatusEnum>(OverlayStatusEnum.Idle);

  refreshOrdersStream$ = new Subject<void>();
  refreshShipmentStream$ = new Subject<void>();
  refreshRoutesStream$ = new Subject<void>();
  mainRefreshStream$ = new Subject<void>();
  OverlayStatusEnum = OverlayStatusEnum;

  lastUpdateDate$ = new BehaviorSubject<Date>(new Date());
  isAutoRefreshEnabled = true;
  isPageHidden = false;
  editedRouteId: number | null = null;
  absencesDisplayedColumns: string[] = ['dateTo', 'typeTitle', 'title', 'objectName'];
  editRouteDialog?: MatDialogRef<any, never>;
  isEditRouteDialogOpen = false;

  ROOT_SCOPE_VARIABLES = ROOT_SCOPE_VARIABLES;
  canDropOrderOnShipmentBox: any;
  DragObjectTypeEnum = DragObjectTypeEnum;

  constructor(private readonly _plannerNativeService: PlannerNativeService,
    private readonly _regionsNativeService: RegionsNativeService,
    private readonly _dictionaryNativeService: DictionaryNativeService,
    private readonly _panelsService: PanelsService,
    private readonly _ordersService: OrdersService,
    private readonly _shipmentsService: ShipmentsService,
    private readonly _deliveryServiceNativeService: DeliveryServiceNativeService,
    private readonly _dialogsService: DialogsService,
    private readonly _messagesService: MessagesService,
    private readonly _schedulingNativeService: SchedulingNativeService,
    private readonly _calendarNativeService: CalendarNativeService,
    private readonly _plannerManagerService: PlannerManagerService,
    private readonly _rootScopeService: RootScopeService,
    private readonly _permissionService: PermissionsService,
    private readonly _router: Router,
    private readonly _dragManager: PlannerDraggingManagerService,
    private readonly _a18sharedDataService: A18SharedDataService,
    private readonly _changeDetectionRef: ChangeDetectorRef,
  ) {
    if (!this._permissionService.isPermissionEnabled(ROOT_SCOPE_VARIABLES.PERMISSIONS.PLANNING_DAILYPLAN.MODULE)) {
      this._router.navigate(['/']);
    }
  }

  @HostListener('document:visibilitychange', ['$event'])
  visibilitychange() {
    this.isPageHidden = document.hidden;
  }

  ngOnInit(): void {
    this.plannerConfiguration$.next({
      ...this.plannerConfiguration$.value,
      clientId: this.clientId,
    });

    this.initNewOrderShipmentListening();
    this.loadDictionaries();
    this.loadDrivers();
    this.loadOrders();
    this.loadShipments();
    this.initPlannerDataLoading();
    this.initDaysHeaderCalculation();
    this.initClientSideSearchControlls();
    this.initAngularJSPanelsLogic();
    this.setPermissionVariables();
    this.initTimerRefresh();
    this.initAutoRefresh();
  }

  ngAfterViewInit() {
    this.mainGridOnDragOverThrottled = throttle(this.mainGridOnDragOver.bind(this), 150);
  }

  ngOnDestroy(): void {
    this._rootScopeService.unsubscribeFromScopeChanges([ROOT_SCOPE_VARIABLES.PLANNER_VIEW_EDIT_ROUTE.MODAL_CLOSE]);
    this._shipmentsService.unsubscribeFromScopeChanges();
    this._ordersService.unsubscribeFromScopeChanges();
    this.stopAutoScroll();
  }

  mainGridOnDragEnd() {
    this.stopAutoScroll();
  }

  mainGridOnDragOverThrottled(_: DragEvent) {
    // nadpisywane w ngAfterViewInit
  }

  pageChanged(goingForward = true): void {
    const { daysMode, firstDay, lastDay } = this.plannerConfiguration$.value;
    const daysOffset = daysMode === DaysModeEnum.DAY ? 1 : 7;
    const adjustedDays = goingForward ? daysOffset : -daysOffset;

    this.plannerConfiguration$.next({
      ...this.plannerConfiguration$.value,
      firstDay: addDays(firstDay, adjustedDays),
      lastDay: addDays(lastDay, adjustedDays),
    });
  }

  daysModeChanged($event: any) {
    const daysModeNewValue = ($event.target as HTMLSelectElement).value as DaysModeEnum;
    const firstDay = daysModeNewValue !== DaysModeEnum.DAY ? getStartOfWeek(this.plannerConfiguration$.value.firstDay) : this.plannerConfiguration$.value.firstDay;

    this.plannerConfiguration$.next({
      ...this.plannerConfiguration$.value,
      daysMode: daysModeNewValue,
      firstDay: firstDay,
      lastDay: addDays(firstDay, PeriodsOffsetMap[daysModeNewValue]),
      middleDayOfThePeriod: MiddleDayMap[daysModeNewValue]
    })
  }

  regionChanged($event: any) {
    const regionId = ($event.target as HTMLSelectElement).value;

    if (!Number.isNaN(regionId)) {
      this.transportAssets$.next({ driversIds: [], trailersIds: [], vehiclesIds: [] });

      this.gridRows$.next([]);

      this._a18sharedDataService.setPlannerSelectedRegion(Number.parseInt(regionId));

      this.plannerConfiguration$.next({
        ...this.plannerConfiguration$.value,
        regionId: Number.parseInt(regionId),
      })
    }
  }

  checkValidationBeforeOrderDropped($event: OrderDroppedEvent, absenceConflictTemplate: TemplateRef<any>): void {
    const absenceConflictsMsg = 'Podczas próby dodania zlecenia wykryto konflikt. Wykryto niedostępność na pojeździe lub kierowcy.';
    const routesConflictsMsg = 'Podczas próby dodania zlecenia wykryto konflikt. Zlecenie, które próbujesz dodać pokrywa się z już istniejącym zleceniem w trasie ';
    const bothConflictsMsg = 'Podczas próby dodania zlecenia wykryto konflikt. Zlecenie, które próbujesz dodać pokrywa się z już istniejącym zleceniem w trasie. Wykryto także niedostępność na pojeździe lub kierowcy.';

    const rowToAddOrder = this.gridRows$.value.find(r => r.driverVehicleInfo.driverId === $event.dropInfo.driver?.driverId
      && r.driverVehicleInfo.vehicleId === $event.dropInfo.driver?.vehicleId
    );

    if (rowToAddOrder) {
      const resultType = this._plannerManagerService.validateRoutesAndAbsencesConflicts($event, rowToAddOrder);

      if (resultType !== 'NONE') {
        const msg = resultType === 'BOTH' ? bothConflictsMsg
          : resultType === 'ABSENCES_CONFLICT' ? absenceConflictsMsg
            : routesConflictsMsg;

        this._dialogsService.openCustomContent({
          header: '',
          cancelButtonText: 'Zamknij',
          template: absenceConflictTemplate,
          context: msg,
          additionalActions: [{ actionId: 'CONFIRMED', closeOnAction: true, text: 'Utwórz', buttonThemeClass: 'theme-button--green' },]
        },
          { width: '800px', maxWidth: '75vw' })
          .afterClosed().pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: result => {
              if (result?.actionId === 'CONFIRMED') {
                this.orderDropped($event);
              }
            }
          });
      } else {
        this.orderDropped($event);
      }
    }
    else {
      this.orderDropped($event);
    }
  }

  orderDropped($event: OrderDroppedEvent): void {
    this._dragManager.isDragInProgress$.next(false);

    const rowToAddOrder = this.gridRows$.value.find(r => r.driverVehicleInfo.driverId === $event.dropInfo.driver?.driverId
      && r.driverVehicleInfo.vehicleId === $event.dropInfo.driver?.vehicleId
    );
    const rowToRemoveOrderFrom = this.gridRows$.value.find(r => r.driverVehicleInfo.driverId === $event.dragInfo.driver?.driverId
      && r.driverVehicleInfo.vehicleId === $event.dragInfo.driver?.vehicleId
    );

    const moveShipmentDto: ShipmentChangeModel = {
      moveShipmentOnPlanDto: { ShipmentId: $event.dragInfo.order.Id }
    };

    let refreshShipmentList = false;

    if (rowToAddOrder) {
      if ($event.dropInfo.day !== undefined) {
        const day = rowToAddOrder.days.find(d => d.id === $event.dropInfo.day!.id);
        if (day) {
          day.type = 'Routes';
          const routeToAdd = $event.dropInfo.route ? $event.dropInfo.route
            : {
              routeId: -1,
              orders: [],
              PlanTime: setToStartOfDay($event.dragInfo.order.StartDate),
              PlanEndTime: setToStartOfNextDay($event.dragInfo.order.EndDate),
              IsMultiDayRoute: false,
            } as RouteModel;

          routeToAdd.orders.push($event.dragInfo.order);

          const diffInDays = differenceInDays($event.dragInfo.order.StartDate, $event.dragInfo.order.EndDate);
          moveShipmentDto.moveShipmentOnPlanDto.PlanTime = setToStartOfDay(day.date);
          moveShipmentDto.moveShipmentOnPlanDto.PlanEndTime = addDays(setToStartOfNextDay(day.date), diffInDays);
          moveShipmentDto.moveShipmentOnPlanDto.DriverId = rowToAddOrder.driverVehicleInfo.driverId;
          moveShipmentDto.moveShipmentOnPlanDto.VehicleId = rowToAddOrder.driverVehicleInfo.vehicleId;
          moveShipmentDto.moveShipmentOnPlanDto.VehicleTrailerId = rowToAddOrder.driverVehicleInfo.trailerId;
          moveShipmentDto.newRoute = routeToAdd;

          if (!$event.dropInfo.route) {
            if (!day.routes) {
              day.routes = [];
            }
            day.routes.push(routeToAdd);
          } else {
            moveShipmentDto.moveShipmentOnPlanDto.NewPlanId = routeToAdd.routeId;
          }
        }
      }
    } else {
      refreshShipmentList = true;
    }

    if (rowToRemoveOrderFrom) {
      if ($event.dragInfo.day !== undefined && $event.dragInfo.route) {
        const day = rowToRemoveOrderFrom.days.find(d => d.id === $event.dragInfo.day.id);
        if (day) {
          const route = day.routes.find(r => r.routeId === $event.dragInfo.route?.routeId);
          const index = route?.orders.findIndex(order => order.Id === $event.dragInfo.order.Id);

          if (index !== undefined && index !== -1) {
            route?.orders.splice(index, 1);
          }

          if (route?.orders.length === 0) {
            const routeIndex = day.routes.findIndex(r => r.routeId === $event.dragInfo.route?.routeId);
            if (routeIndex !== undefined && routeIndex !== -1) {
              day.routes.splice(routeIndex, 1);
            }
          }

          if (day.routes.length === 0 && (day.absences?.length === 0)) {
            day.type = 'Empty';
          }

          moveShipmentDto.moveShipmentOnPlanDto.OldPlanId = route?.routeId;
        }
      }
    }
    else {
      this.availableShipments$.next(this.availableShipments$.value.filter(order => order.Id !== $event.dragInfo.order.Id));
    }

    this.modifyShipment(moveShipmentDto, refreshShipmentList);

    if (rowToAddOrder) {
      this._plannerManagerService.checkIfRowExceededDesigatedHeight(rowToAddOrder);
    }

    if (rowToRemoveOrderFrom) {
      this._plannerManagerService.checkIfRowExceededDesigatedHeight(rowToRemoveOrderFrom);
    }
  }

  expandAll() {
    for (let row of this.gridRows$.value) {
      this.expandRow(row);
    }
  }

  collapseAll() {
    for (let row of this.gridRows$.value) {
      this.collapseRow(row);
    }
  }

  expandDay(row: GridRowModel, day: DayModel): void {
    day.isDayExpanded = true;
    row.isRowExpanded = true;
  }

  expandRow(row: GridRowModel): void {
    row.isRowExpanded = true;
    for (let day of row.days) {
      day.isDayExpanded = true;
    }
  }

  collapseRow(row: GridRowModel): void {
    row.isRowExpanded = false;
    for (let day of row.days) {
      day.isDayExpanded = false;
    }
  }

  categoryChanged($event: MatButtonToggleChange): void {
    this.vehicleCategoryFilter$.value?.forEach(c => c.isSelected = c.ids === $event.value);

    this.transportAssets$.next({ driversIds: [], trailersIds: [], vehiclesIds: [] });
    this.gridRows$.next([]);

    this.plannerConfiguration$.next({
      ...this.plannerConfiguration$.value,
      categoryId: $event.value
    });
  }
  showNewOrderPanel($event: MouseEvent): boolean {
    $event.stopPropagation();
    this.overlayStatus$.next(OverlayStatusEnum.Overlay);
    this._ordersService.openNewOrderPanel();
    return true;
  }

  showNewShipmentPanel($event: MouseEvent): boolean {
    $event.stopPropagation();
    this.overlayStatus$.next(OverlayStatusEnum.Overlay);
    this._shipmentsService.openNewShipmentPanel();
    return true;
  }

  checkIfClickedOnBackdrop($event: MouseEvent) {
    if (this.overlayStatus$.value === OverlayStatusEnum.Overlay) {
      $event.preventDefault();
      this._panelsService.closeOrderPanel();
      this._panelsService.closeShipmentPanel();
      this.overlayStatus$.next(OverlayStatusEnum.Idle);
    }
  }

  editOrder($event: OrderModel): void {
    this.overlayStatus$.next(OverlayStatusEnum.Working);
    this._deliveryServiceNativeService.getOrderManagement({ id: $event.Id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (order) => {
          this._ordersService.openOrderDetailsPanel(order);
          this.overlayStatus$.next(OverlayStatusEnum.Overlay);
        },
        error: () => this.overlayStatus$.next(OverlayStatusEnum.Idle)
      }
      )
  }


  editShipmentOnRoute($event: { shipment: OrderModel, route: RouteModel }): void {
    this.overlayStatus$.next(OverlayStatusEnum.Working);
    this._deliveryServiceNativeService.getNewShipmentDetails({ id: $event.shipment.Id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (shipment) => {
          this._shipmentsService.openShipmentDetailsPanel(shipment);
          this.overlayStatus$.next(OverlayStatusEnum.Overlay);
          this.editedRouteId = $event.route.routeId;
        },
        error: () => this.overlayStatus$.next(OverlayStatusEnum.Idle)
      }
      )
  }

  editShipment($event: OrderModel): void {
    this.overlayStatus$.next(OverlayStatusEnum.Working);
    this._deliveryServiceNativeService.getNewShipmentDetails({ id: $event.Id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (shipment) => {
          this._shipmentsService.openShipmentDetailsPanel(shipment);
          this.overlayStatus$.next(OverlayStatusEnum.Overlay);
        },
        error: () => this.overlayStatus$.next(OverlayStatusEnum.Idle)
      }
      )
  }

  refreshRoutesClick(): void {
    this.mainRefreshStream$.next();
  }

  routeEditCLick(route: RouteModel, templateRef: TemplateRef<any>): void {
    this.selectedRouteChanged.emit(route.routeId);
    this.isEditRouteDialogOpen = true;
    this.editRouteDialog = this.dialog.open(templateRef, {
      restoreFocus: false,
      hasBackdrop: true,
      width: '85vw',
      maxWidth: '85vw',
    });

    this.editRouteDialog!.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)
      , tap(() => this.selectedRouteChanged.emit(-1)) // zerujemy ID, tak żeby przy nastepnym otwarciu okna znowu się pobrało, nawet na tym samym zgłoszeniu
      , switchMap(() => {
        this.overlayStatus$.next(OverlayStatusEnum.Working);
        return this._plannerNativeService.getRoutesByIds({ plansIds: [route.routeId] });
      })).subscribe({
        next: (result) => {
          this.isEditRouteDialogOpen = false;
          this.reloadGridRows(result);
          this.overlayStatus$.next(OverlayStatusEnum.Idle);
        },
        error: () => {
          this.overlayStatus$.next(OverlayStatusEnum.Idle)
          this.isEditRouteDialogOpen = false;

        }
      });
  }

  sendRoutesToDrivers(): void {
    this._dialogsService.openConfirmation({ confirmButtonText: 'Wyślij', message: 'Czy chcesz wysłać trasy do kierowców?' })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          if (result?.wasConfirmed) {
            const plansToSend = this.gridRowsFiltered$.value
              .flatMap(rows => rows.days.flatMap(d => d.routes))
              .filter(route => route.orders.length > 0);

            this.sendPlansToMobile(plansToSend);
          }
        }
      });
  }

  onDragOnShipmentOver($event: any) {
    $event.preventDefault();

    if (!this.canDropOrderOnShipmentBox) {
      return;
    }

    const routeId = this._dragManager.getDragRouteId($event, DragObjectTypeEnum.SHIPMENT);
    const isShipmentDragging = this._dragManager.isShipmentDraging($event);
    const isOrderDragging = this._dragManager.isOrderDraging($event);

    if (routeId !== null && isShipmentDragging) {
      this.isShipmentDragOverShipments = true;
    } else if (isOrderDragging) {
      this.isOrderDragOverShipments = true;
    }
  }

  onDragOnShipmentLeave() {
    this.isShipmentDragOverShipments = false;
    this.isOrderDragOverShipments = false;
  }

  onDragOnShipmentEnter($event: any) {
    $event.preventDefault();

    const routeId = this._dragManager.getDragRouteId($event, DragObjectTypeEnum.SHIPMENT);
    const isShipmentDragging = this._dragManager.isShipmentDraging($event);
    const isOrderDragging = this._dragManager.isOrderDraging($event);


    if (routeId !== null && isShipmentDragging) {
      this.isShipmentDragOverShipments = true;
    } else if (isOrderDragging) {
      if (!this.canDropOrderOnShipmentBox) {
        return;
      }

      this.isOrderDragOverShipments = true;
    }
  }


  onDropOnShipment($event: any) {
    $event.preventDefault();
    this._dragManager.isDragInProgress$.next(false);

    if (this.isShipmentDragOverShipments) {
      this.isShipmentDragOverShipments = false;
      const orderDragInfo = this.getDragInfo($event);

      if (orderDragInfo) {
        const dropEvent: OrderDroppedEvent = {
          dragInfo: orderDragInfo,
          dropInfo: {}
        }
        this.orderDropped(dropEvent)
      }
    }

    if (this.isOrderDragOverShipments) {
      this.isOrderDragOverShipments = false;

      if (!this.canDropOrderOnShipmentBox) {
        return;
      }

      const orderDragInfo = this.getDragInfo($event, true);

      if (orderDragInfo?.order.Id) {
        this.overlayStatus$.next(OverlayStatusEnum.Working);
        this._plannerNativeService.newShipmentByOrder({ OrderId: orderDragInfo?.order.Id })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.overlayStatus$.next(OverlayStatusEnum.Idle);
              this.refreshShipmentStream$.next();
              this.refreshOrdersStream$.next();
            },
            error: () => this.overlayStatus$.next(OverlayStatusEnum.Idle)
          })
      }
    }
  }

  getLoadAbsenceStream(transportAssets: { driversIds: number[]; vehiclesIds: number[]; trailersIds: number[]; }): Observable<ScheduleAbsenceDto[]> {

    const conf = this.plannerConfiguration$.value;

    const driversIds = transportAssets.driversIds;
    const vehiclesIds = transportAssets.vehiclesIds;
    const trailersIds = transportAssets.trailersIds;

    const driverPairs = driversIds.map(driverId => ({ ObjectId: driverId, ObjectType: BusinessObjectType.DRIVER } as ObjectIDModel));
    const vehiclesPairs = vehiclesIds.map(vehicleId => ({ ObjectId: vehicleId, ObjectType: BusinessObjectType.VEHICLE } as ObjectIDModel));
    const trailersPairs = trailersIds.filter(t => t !== undefined && t !== null).map(trailerId => ({ ObjectId: trailerId, ObjectType: BusinessObjectType.VEHICLE } as ObjectIDModel));

    return this._schedulingNativeService.getAbsencesForObjects({
      ClientId: conf.clientId,
      DateStart: conf.firstDay,
      DateEnd: conf.lastDay,
      Objects: driverPairs.concat(vehiclesPairs.concat(trailersPairs))
    });
  }

  getCalendarEventsStream(transportAssets: { driversIds: number[]; vehiclesIds: number[]; trailersIds: number[]; }): Observable<EventDto[]> {

    const conf = this.plannerConfiguration$.value;
    const vehiclesIds = transportAssets.vehiclesIds;


    return this._calendarNativeService.getObjectEvents({
      DateFrom: conf.firstDay,
      DateTo: conf.lastDay,
      ObjectTypeId: BusinessObjectType.VEHICLE,
      ObjectsIds: vehiclesIds
    });
  }

  showDayConflictsClick($event: DayModel, absencesConflictTemplate: TemplateRef<any>): void {
    this._dialogsService.openCustomContent({
      header: 'Konflikt ',
      message: 'Występuje konflikt pomiędzy trasami a następującymi niedostępnościami:',
      cancelButtonText: 'Zamknij',
      context: $event.absences,
      template: absencesConflictTemplate
    })
  }

  addNewAbsence($event: AddNewAbsenceEvent, day: DayModel, driver: DriverVehiclePairModel, row: GridRowModel): void {
    const data: AddAbsenceDialogDataModel = {
      objectType: $event.objectType,
      inaccessibilityStatus: this.inaccessibilityStatus$.value || [],
      inaccessibilityType: this.inaccessibilityType$.value || [],
      startDate: day.date,
    };

    this._dialogsService.openPopupForCustomComponent<AddAbsenceDialogDataModel, AddAbsenceDialogResponseModel>({
      component: AddAbsenceForPlannerComponent,
      data: data
    }, { maxWidth: '600px' }).afterClosed()
      .pipe(filter(result => result != undefined),
        tap(() => this.overlayStatus$.next(OverlayStatusEnum.Working)),
        mergeMap(result => {
          return this._schedulingNativeService.addAbsenceForObject({
            ClientId: this.plannerConfiguration$.value.clientId,
            ObjectId: $event.objectId,
            ObjectType: result!.objectType,
            DateStart: setToStartOfDay(result!.absenceData.dateStart),
            DateEnd: setToEndOfDay(result!.absenceData.dateEnd),
            Description: result!.absenceData.description,
            ScheduleStatusId: result!.absenceData.scheduleStatus,
            ScheduleTypeId: result!.absenceData.scheduleType,
            IsActive: true,
          });
        }))
      .subscribe(
        {
          next: (result: ScheduleAbsenceDto) => {
            if (result) {
              const objectName = $event.objectType === BusinessObjectType.DRIVER ? driver.driverDisplayName : driver.vehicleName;
              const scheduleType = this.inaccessibilityType$.value?.find(v => v.Id === result.ScheduleTypeId);
              // UZUPEŁNIAMY DANE OBIEKTU - NORMALNIE PRZYCHODZĄ Z BACKENDU, ALE JAK DODAJEMY RĘCZNIE TO TRZEBA DTO UZUPEŁNNIĆ 
              result.ObjectName = objectName;
              result.ScheduleType = scheduleType?.Title || '';

              this._plannerManagerService.applyAbsenceToRows([row], result, objectName, scheduleType?.Title || '');
              this._plannerManagerService.updateAbsenceListWithNewlyAddedAbsence(result);

              this.gridRows$.next(cloneDeep(this.gridRows$.value));
              this.overlayStatus$.next(OverlayStatusEnum.Idle);
              this._changeDetectionRef.markForCheck();
            }
          },
          error: () => this.overlayStatus$.next(OverlayStatusEnum.Idle)
        });
  }

  sendRouteToDriver($event: RouteModel): void {
    this.sendPlansToMobile([$event]);
  }

  plannerDateRangeChanged($event: { start: Date, end: Date }) {
    this.plannerConfiguration$.next({
      ...this.plannerConfiguration$.value,
      firstDay: $event.start,
      lastDay: addDays($event.end, 1), // w planerze lastdate to godzina 00:00 nastepnego dnia, więc datę z kalendarza musimy dać +1 
    })
  }

  private initNewOrderShipmentListening() {
    this._ordersService.orderCreated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.refreshOrdersStream$.next();
        }
      });

    this._shipmentsService.shipmentCreated$
      .pipe(takeUntilDestroyed(this.destroyRef),
        switchMap(() =>
          // jeśli zmienna editedRouteId jest ustawiona to znaczy że musimy odświeżyć plan po jego edycji
          iif(
            () => !!this.editedRouteId,
            this._plannerNativeService.getRoutesByIds({ plansIds: [this.editedRouteId!] }),
            of([])
          )
        ))
      .subscribe({
        next: (routesToReload) => {
          this.editedRouteId = null;

          if (routesToReload && routesToReload.length > 0) {
            this.reloadGridRows(routesToReload)
          } else {
            this.refreshShipmentStream$.next();
          }
        }
      });
  }

  private initAngularJSPanelsLogic(): void {
    this._panelsService.panels$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (panels: Panel) => {
          this.orderPanelsChange.emit(panels);
          if (!panels.isOpenLeft && this.overlayStatus$.value !== OverlayStatusEnum.Idle) {
            this.overlayStatus$.next(OverlayStatusEnum.Idle);
          }
        }
      });

    this._panelsService.shipmentPanels$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (panels: Panel) => {
          this.shipmentPanelsChange.emit(panels);
          if (!panels.isOpenLeft && this.overlayStatus$.value !== OverlayStatusEnum.Idle) {
            this.overlayStatus$.next(OverlayStatusEnum.Idle);
            this.editedRouteId = null;

          }
        }
      });

    this._rootScopeService.detectScopeChanges(ROOT_SCOPE_VARIABLES.PLANNER_VIEW_EDIT_ROUTE.MODAL_CLOSE);
    this._rootScopeService.rootScopeObservable
      .pipe(takeUntilDestroyed(this.destroyRef),
        filter((rootScope) => rootScope[ROOT_SCOPE_VARIABLES.PLANNER_VIEW_EDIT_ROUTE.MODAL_CLOSE] === true)
      ).subscribe(() => {
        this.editRouteDialog!.close();
      })
  }

  private initClientSideSearchControlls(): void {
    this.availableOrdersFiltered$ = combineLatest([
      this.availableOrders$,
      this.ordersSearchControl.valueChanges.pipe(debounceTime(400), startWith(''), map(s => s ? s.toLowerCase() : '')),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef),
        map(([orders, searchCriteria]) => {
          let results = [...orders];

          return searchCriteria ?
            results.filter(order =>
              order.EndAddressNormalized.includes(searchCriteria)
              || order.StartAddressNormalized.includes(searchCriteria)
              || order.OrderNumberNormalized.includes(searchCriteria)
              || order.DeliveryClientNameNormalized.includes(searchCriteria)
              || order.DeliveryContractorNameNormalized.includes(searchCriteria))
            : results;
        }));

    this.availableShipmentsFiltered$ = combineLatest([
      this.availableShipments$,
      this.ordersSearchControl.valueChanges.pipe(debounceTime(400), startWith(''), map(s => s ? s.toLowerCase() : '')),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef),
        map(([shipments, searchCriteria]) => {
          let results = [...shipments];

          return searchCriteria ?
            results.filter(order => this.filterOrderBySearchCriteria(order, searchCriteria))
            : results;
        }));

    combineLatest([
      this.gridRows$,
      this.driverSearchControl.valueChanges.pipe(debounceTime(400), startWith(''), map(s => s ? s.toLowerCase() : '')),
      this.mainSearchControl.valueChanges.pipe(debounceTime(400), startWith(''), map(s => s ? s.toLowerCase() : '')),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef),
        map(([gridRows, driverSearchCriteria, mainSearchCriteria]) => {

          let results = [...gridRows];

          if (driverSearchCriteria) {
            results = results.filter(gridRow => this.filterDriverAndVehicles(gridRow, driverSearchCriteria));
          }

          if (mainSearchCriteria) {
            const newResults: GridRowModel[] = [];
            for (let gridRow of results) {
              const newDays = gridRow.days.map(day => {
                if (day.type !== 'Routes') {
                  return day;
                } else {
                  const newRoutes = day.routes.filter(route => {
                    const filteredOrders = route.orders.filter(order => this.filterOrderBySearchCriteria(order, mainSearchCriteria));

                    if (filteredOrders.length > 0) {
                      return { ...route, orders: filteredOrders } as RouteModel;
                    }
                    else {
                      return null;
                    }
                  }).filter(r => r);

                  if (newRoutes.length > 0) {
                    return { ...day, routes: newRoutes } as DayModel;
                  } else {
                    return { ...day, routes: [], type: 'Empty' } as DayModel;
                  }
                }
              })

              const newRow: GridRowModel = {
                ...gridRow,
                days: newDays,
              };

              if (newRow.days.some(day => day.type === 'Routes')) {
                newResults.push(newRow);
              }
            }
            results = [...newResults]
          }

          return results;
        })).subscribe({
          next: filteredRows => {
            this.gridRowsFiltered$.next(filteredRows);
          }
        });
  }

  private filterDriverAndVehicles(gridRow: GridRowModel, driverSearchCriteria: string): unknown {
    return gridRow.driverVehicleInfo.driverDisplayNameNormalized.includes(driverSearchCriteria)
      || gridRow.driverVehicleInfo.trailerRegistrationNumberNormalized.includes(driverSearchCriteria)
      || gridRow.driverVehicleInfo.registrationNumberNormalized.includes(driverSearchCriteria);
  }

  private filterOrderBySearchCriteria(order: OrderModel, mainSearchCriteria: string): boolean {
    return order.EndAddressNormalized.includes(mainSearchCriteria)
      || order.StartAddressNormalized.includes(mainSearchCriteria)
      || order.OrderNumberNormalized.includes(mainSearchCriteria)
      || order.DeliveryClientNameNormalized.includes(mainSearchCriteria)
      || order.DeliveryContractorNameNormalized.includes(mainSearchCriteria);
  }

  private initDaysHeaderCalculation(): void {
    this.plannerConfiguration$.pipe(takeUntilDestroyed(this.destroyRef),
      map(conf => this._plannerManagerService.generateDaysHeaderObjects(conf.firstDay, conf.daysMode)))
      .subscribe({
        next: v => {
          this.displayedDays$.next(v)
        }
      });
  }

  private initPlannerDataLoading(): void {
    combineLatest(
      [this.transportAssets$, this.plannerConfiguration$, this.refreshRoutesStream$.pipe(startWith(() => { }))])
      .pipe(
        filter(([transportAssets, _]) => transportAssets.driversIds.length > 0),
        tap(() => this.overlayStatus$.next(OverlayStatusEnum.Working)),
        switchMap(([transportAssets, plannerConfiguration]) =>
          forkJoin([
            this._plannerNativeService.getDriversRoutes({
              fromDate: plannerConfiguration.firstDay,
              toDate: plannerConfiguration.lastDay,
              driversIds: transportAssets.driversIds,
              categoryIds: plannerConfiguration.categoryId!
            }),
            this.getLoadAbsenceStream(transportAssets),
            this.getCalendarEventsStream(transportAssets)])
            .pipe(map(([routes, absencesResponse, calendarsResponse]) => ({ routes, plannerConfiguration, absencesResponse, calendarsResponse })))
        ),
      )
      .subscribe({
        next: (resp) => {

          const updatedRows = this._plannerManagerService.generateRoutesForDrivers(this.gridRows$.value, resp.routes, resp.plannerConfiguration);
          const rowsWithAbsences = this._plannerManagerService.mergeAbsencesResponse(updatedRows, resp.absencesResponse, resp.calendarsResponse);
          this.gridRows$.next(rowsWithAbsences);
          this.overlayStatus$.next(OverlayStatusEnum.Idle);
          this.lastUpdateDate$.next(new Date());
        }, error: () => this.overlayStatus$.next(OverlayStatusEnum.Idle)
      }
      )
  }

  private loadDrivers(): void {
    this.plannerConfiguration$.pipe(
      filter(conf => this.configurationFullyLoaded(conf)),
      distinctUntilChanged((prev, curr) => this.checkConfigurationForDriverReload(prev, curr)),
      switchMap(
        (conf) => this._plannerNativeService.getDrivers({ clientId: conf.clientId, categoryIds: conf.categoryId!, regionsIds: conf.regionId ? [conf.regionId!] : [] })
      ),
      withLatestFrom(this.regions$.pipe(filter(r => r !== null))),
      takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([drivers, regions]) => {
          if (drivers && drivers.length && regions) {
            const flatternResponse = drivers.flatMap(set => set.Vehicles.map(vehicle => ({
              driver: set.Driver,
              vehicle: vehicle,
            })));

            const allTrailers = drivers.flatMap(set => set.Trailers);

            const driverRows = flatternResponse.map(driverVehiclePair => {
              return {
                driverVehicleInfo: mapDriverResponseToUIResponse(driverVehiclePair.driver, driverVehiclePair.vehicle, allTrailers.find(t => t.DriverId === driverVehiclePair.driver.Id), regions),
                days: [{ type: 'None' }, { type: 'None' }, { type: 'None' }, { type: 'None' }, { type: 'None' }, { type: 'None' }, { type: 'None' },]
              } as GridRowModel;
            });

            this.transportAssets$.next({
              driversIds: [...new Set(driverRows.map(d => d.driverVehicleInfo.driverId))],
              trailersIds: [...new Set(driverRows.map(d => d.driverVehicleInfo.vehicleId))],
              vehiclesIds: [...new Set(driverRows.filter(d => d.driverVehicleInfo.trailerId).map(d => d.driverVehicleInfo.trailerId!))]
            });

            this.gridRows$.next(driverRows);
          }
        }
      });
  }

  private loadOrders(): void {
    combineLatest(
      [this.plannerConfiguration$,
      this.refreshOrdersStream$.pipe(startWith(() => { }))
      ])
      .pipe(
        filter(([conf]) => this.configurationFullyLoaded(conf)),
        tap(() => this.isLoadingOrders = true),
        switchMap(([conf]) =>
          this._plannerNativeService.getUnassignedOrders({
            clientId: conf.clientId,
            fromDate: conf.firstDay,
            toDate: conf.lastDay,
            categoryIds: conf.categoryId!,
            regionsIds: conf.regionId ? [conf.regionId!] : []
          })),
        withLatestFrom(this.taskOrderTypeSet$.pipe(filter(dictionary => dictionary !== null)),
          this.orderStatusSet$.pipe(filter(dictionary => dictionary !== null))),
        takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([orders, taskOrderTypeSet, orderStatusSet]) => {
          if (orders && taskOrderTypeSet && orderStatusSet) {
            this.availableOrders$.next(mapOrderResponseToUIResponse(orders, taskOrderTypeSet, orderStatusSet));
            this.isLoadingOrders = false;
          }
        },
        error: () => this.isLoadingOrders = false
      });
  }

  private loadShipments(): void {
    combineLatest([
      this.plannerConfiguration$,
      this.refreshShipmentStream$.pipe(startWith(() => { }))
    ])
      .pipe(
        filter(([conf]) => this.configurationFullyLoaded(conf)),
        tap(() => this.isLoadingShipments = true),
        switchMap(([conf]) =>
          this._plannerNativeService.getUnassignedShipments({
            clientId: conf.clientId,
            fromDate: conf.firstDay,
            toDate: conf.lastDay,
            categoryIds: conf.categoryId!,
            regionsIds: conf.regionId ? [conf.regionId!] : []
          })),
        withLatestFrom(this.taskOrderTypeSet$.pipe(filter(dictionary => dictionary !== null)),
          this.orderStatusSet$.pipe(filter(dictionary => dictionary !== null))),
        takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([shipments, taskOrderTypeSet, orderStatusSet]) => {
          if (shipments && taskOrderTypeSet && orderStatusSet) {
            this.availableShipments$.next(mapOrderResponseToUIResponse(shipments, taskOrderTypeSet, orderStatusSet));
            this.isLoadingShipments = false;
          }
        }, error: () => this.isLoadingShipments = false
      });
  }

  private loadDictionaries(): void {
    this._dictionaryNativeService.getDictionaryByKey({ key: DictionaryKeyEnum.TASK_ORDER_TYPE })
      .pipe(takeUntilDestroyed(this.destroyRef), map(resp => this._dictionaryNativeService.mapDictionaryById(resp.Items)))
      .subscribe({
        next: taskOrderTypeSet => {
          this._plannerManagerService.init({ taskOrderTypeSet });
          this.taskOrderTypeSet$.next(taskOrderTypeSet)
        }
      });

    this._dictionaryNativeService.getDictionaryByKey({ key: DictionaryKeyEnum.ORDER_STATUS })
      .pipe(takeUntilDestroyed(this.destroyRef), map(resp => this._dictionaryNativeService.mapDictionaryById(resp.Items)))
      .subscribe({
        next: orderStatusSet => {
          this._plannerManagerService.init({ orderStatusSet });
          this.orderStatusSet$.next(orderStatusSet)

        }
      });

    this._dictionaryNativeService.getDictionaryByKey({ key: DictionaryKeyEnum.VEHICLE_CATEGORY })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resp => {
          const vehicleCategorySet = this._dictionaryNativeService.mapDictionaryById(resp.Items);
          this._plannerManagerService.init({ vehicleCategorySet });
          this.vehicleCategory$.next(vehicleCategorySet);
          this.vehicleCategoryList$.next(resp.Items);
        }
      });


    this._regionsNativeService.getRegions({ clientId: this.plannerConfiguration$.value.clientId })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({ next: regions => this.regions$.next(regions) });


    this.vehicleCategoryList$.pipe(takeUntilDestroyed(this.destroyRef)
      , filter(cat => cat !== null)
      , map(cat => cat!.filter(c => predefinedCategoryKeyNames.includes(c.KeyName))))
      .subscribe({
        next: cat => {

          const restOfTheCategories = cat!.filter(c => !soloAndBusCategoryFilter.keyNames.includes(c.KeyName));
          const soloAndBusFromBackend = cat!.filter(c => soloAndBusCategoryFilter.keyNames.includes(c.KeyName));

          soloAndBusCategoryFilter.ids = soloAndBusFromBackend.map(c => c.Id);
          soloAndBusCategoryFilter.title = soloAndBusFromBackend.map(c => c.Title).join(' & ');

          this.plannerConfiguration$.next({
            ...this.plannerConfiguration$.value,
            categoryId: soloAndBusCategoryFilter.ids
          });

          this.vehicleCategoryFilter$.next([
            soloAndBusCategoryFilter,
            ...restOfTheCategories!.map(d => ({ ids: [d.Id], keyNames: [d.KeyName], title: d.Title } as CategoryFilterType))
          ])
        }
      });


    this._schedulingNativeService.getSchedulingDictionaries({ clientId: this.plannerConfiguration$.value.clientId })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: response => {
          this.inaccessibilityType$.next(response.SchedulingDictionary['scheduling.inaccessibility.type']);
          this.inaccessibilityStatus$.next(response.SchedulingDictionary['scheduling.inaccessibility.status']);
        }
      });
  }

  private checkConfigurationForDriverReload(prev: PlannerConfiguration, curr: PlannerConfiguration): boolean {
    return prev.categoryId === curr.categoryId && prev.regionId === curr.regionId; // ture = object are same & no need to reload
  }

  private configurationFullyLoaded(conf: PlannerConfiguration): boolean {
    return !!conf.categoryId && conf.categoryId.length > 0;
  }

  private modifyShipment(changeModel: ShipmentChangeModel, refreshShipmentList: boolean) {
    this.overlayStatus$.next(OverlayStatusEnum.Working);
    const plansToRefresh = new Array<number>();
    if (changeModel.moveShipmentOnPlanDto.OldPlanId) {
      plansToRefresh.push(changeModel.moveShipmentOnPlanDto.OldPlanId);
    }
    if (changeModel.moveShipmentOnPlanDto.NewPlanId) {
      plansToRefresh.push(changeModel.moveShipmentOnPlanDto.NewPlanId);
    }

    this._plannerNativeService.moveShipmentOnPlan(changeModel.moveShipmentOnPlanDto)
      .pipe(takeUntilDestroyed(this.destroyRef),
        mergeMap(result => {
          if (changeModel.newRoute) {
            changeModel.newRoute.routeId = result.NewPlanId;
            changeModel.newRoute.PlanTime = new Date(result.PlanTime);
            changeModel.newRoute.PlanEndTime = new Date(result.PlanEndTime);
            plansToRefresh.push(result.NewPlanId);
          }

          return this._plannerNativeService.getRoutesByIds({ plansIds: plansToRefresh });
        })
      )
      .subscribe({
        next: result => {
          if (result && result.length > 0) {
            this.reloadGridRows(result);
          }


          if (refreshShipmentList) {
            this.refreshShipmentStream$.next()
          }
          this.overlayStatus$.next(OverlayStatusEnum.Idle);
        }, error: () => this.overlayStatus$.next(OverlayStatusEnum.Idle)
      });
  }

  private getDragInfo($event: any, isForOrder = false): OrderDragModel | null {
    const objectType = isForOrder ? DragObjectTypeEnum.ORDER : DragObjectTypeEnum.SHIPMENT;
    return this._dragManager.getDragInfo($event, objectType);
  }

  private reloadGridRows(routesToReload: PlannerRouteDto[]) {
    const updatedRows = this._plannerManagerService.updateRoutesInPlan(this.gridRows$.value, routesToReload, this.plannerConfiguration$.value);
    this.gridRows$.next(updatedRows);
  }

  private sendPlansToMobile(plansToSend: RouteModel[]) {
    if (plansToSend.length > 0) {
      this.overlayStatus$.next(OverlayStatusEnum.Working);

      const uniquePlansId = new Set(plansToSend.map(r => r.routeId));
      this._plannerNativeService.finishPlans({ plansIds: [...uniquePlansId] })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.overlayStatus$.next(OverlayStatusEnum.Idle);

            this._messagesService.pushMessage({
              message: 'Zakończono wysyłkę do aplikacji mobilnej',
            });
          },
          error: () => this.overlayStatus$.next(OverlayStatusEnum.Idle),
        });
    }
  }

  private setPermissionVariables() {
    this.canDropOrderOnShipmentBox = this._permissionService.isPermissionEnabled(ROOT_SCOPE_VARIABLES.PERMISSIONS.PLANNING_DAILYPLAN.PLANNING.ADDORDERS);
  }

  private initAutoRefresh(): void {
    interval(1000).pipe(
      takeUntilDestroyed(this.destroyRef),
      filter(() => this.isAutoRefreshEnabled && !this.isPageHidden && !this.isEditRouteDialogOpen),
      filter(() => this.overlayStatus$.value === OverlayStatusEnum.Idle),
      filter(() => this._dragManager.isDragInProgress$.value === false),
      filter(() => this.transportAssets$.value.driversIds.length > 0),
    ).subscribe(() => {
      const diffInMs = new Date().getTime() - this.lastUpdateDate$.value.getTime();

      if (diffInMs > 50000) {
        this.mainRefreshStream$.next();
      }
    });
  }

  private initTimerRefresh(): void {
    this.mainRefreshStream$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.refreshOrdersStream$.next();
      this.refreshShipmentStream$.next();
      this.refreshRoutesStream$.next();
    })
  }

  private mainGridOnDragOver(event: DragEvent) {
    event.preventDefault();

    const container = this.scrollContainerRef.nativeElement as HTMLElement;
    const bounds = container.getBoundingClientRect();
    const y = event.clientY;

    const threshold = 180; // ile px od góry/dół zaczyna się auto-scroll
    const scrollSpeed = 18; // px per tick

    if (y < bounds.top + threshold) {
      this.startAutoScroll(container, -scrollSpeed);
    }
    else if (y > bounds.bottom - threshold) {
      this.startAutoScroll(container, scrollSpeed);
    } else {
      this.stopAutoScroll();
    }
  }

  private startAutoScroll(container: HTMLElement, speed: number) {
    if (this.scrollInterval) return;

    this.scrollInterval = setInterval(() => {
      container.scrollTop += speed;
    }, 20);
  }

  private stopAutoScroll() {
    if (this.scrollInterval) {
      clearInterval(this.scrollInterval);
      this.scrollInterval = null;
    }
  }
}

